import React from 'react';
import renderPatternDetection from './renderPatternDetection.js';
import renderSuggestions from './renderSuggestions.js';
import renderLearningPlan from './renderLearningPlan.js';
import renderEnhancedAnalysis from './renderEnhancedAnalysis.js';
import  renderConversationalCoach  from './renderConversationalCoach.js';
import { trackAICoachQuerySent, trackAICoachResponseReceived } from '../../services/analytics';

const predefinedQueries = {
    patterns: [
      "What patterns do you see in my trading behavior?",
      "Do I over-leverage after losing trades?",
      "Do I trade differently on certain days of the week?",
      "What are my common trading mistakes?"
    ],
    coaching: [
      "Review my last 5 losing trades - what went wrong?",
      "How can I improve my risk management?",
      "Why do I keep making the same mistakes?",
      "Help me understand my emotional trading patterns"
    ],
    suggestions: [
      "What should I focus on to improve my trading?",
      "Give me personalized recommendations",
      "How can I optimize my trading schedule?",
      "What specific areas need work?"
    ],
    plans: [
      "Create a learning plan for this week",
      "What should my trading focus be this month?",
      "Help me set improvement goals",
      "Design a practice routine for me"
    ]
  };
const AIAnalysis = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const userId = currentUser?.id?.id

  const detectQueryCategory = (q) => {
    const all = Object.entries(predefinedQueries);
    for (const [cat, qs] of all) {
      if (qs.includes(q)) return cat;
    }
    return 'custom';
  };

  const handleSearch = async (searchQuery = query) => {
    if (!searchQuery.trim()) return;
    const queryCategory = detectQueryCategory(searchQuery);
    trackAICoachQuerySent({ query_category: queryCategory, query_length: searchQuery.length });
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/v1/search/trading-history', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({
          query: searchQuery,
          limit: 20
        })
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      trackAICoachResponseReceived({ query_category: queryCategory, response_type: data.type || 'answer' });
      setResults(data);
      setQuery(searchQuery);

      // If it's a reflective coaching response, add to conversation history
      if (data.type === 'reflective_coaching') {
        setConversationHistory(prev => [
          ...prev,
          { role: 'user', content: searchQuery },
          { role: 'assistant', content: data.response || data.answer }
        ]);
      }
    } catch (error) {
      console.error('Search error:', error);
      setError(error.message || 'Search failed. Please try again.');
      setResults(null);
    } finally {
      setLoading(false);
    }
  };
const handleFollowUp = (followUpQuery) => {
    setQuery(followUpQuery);
    handleSearch(followUpQuery);
  };

    <div className="pt-16 min-h-screen bg-background text-foreground">
     <div className="advanced-ai-interface p-6 max-w-7xl mx-auto">
      <h2 className="text-3xl font-bold mb-6 text-foreground">🤖 AI Trading Analysis</h2>

      {/* Search Input */}
      <div className="search-input mb-6">
        <div className="flex gap-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ask about patterns, get coaching, or request personalized suggestions..."
            className="flex-1 p-4 border border-border bg-input text-foreground rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-lg placeholder:text-muted-foreground"
            onKeyPress={(e) => e.key === 'Enter' && !loading && handleSearch()}
            disabled={loading}
          />
          <button
            onClick={() => handleSearch()}
            disabled={loading || !query.trim()}
            className="px-8 py-4 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold"
          >
            {loading ? 'Analyzing...' : 'Analyze'}
          </button>
        </div>
      </div>

      {/* Predefined Query Categories */}
      <div className="predefined-queries mb-8">
        <h3 className="text-xl font-semibold mb-4 text-foreground">Choose an analysis type:</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Object.entries(predefinedQueries).map(([category, queries]) => (
            <div key={category} className="category-group">
              <h4 className="font-semibold text-foreground mb-2 capitalize flex items-center">
                {category === 'patterns' && '🔍 '}
                {category === 'coaching' && '💬 '}
                {category === 'suggestions' && '💡 '}
                {category === 'plans' && '📚 '}
                {category}
              </h4>
              <div className="space-y-2">
                {queries.map((query, index) => (
                  <button
                    key={index}
                    onClick={() => handleSearch(query)}
                    disabled={loading}
                    className="w-full text-left px-3 py-2 text-sm bg-secondary hover:bg-secondary/80 rounded-lg transition-colors disabled:opacity-50 border border-border text-foreground"
                  >
                    {query}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-message mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg">
          <p className="text-destructive">❌ {error}</p>
        </div>
      )}

      {/* Results */}
      {results && (
        <div className="results-container">
          {results.type === 'pattern_observation' && renderPatternDetection(results)}
          {results.type === 'reflective_coaching' && renderConversationalCoach(results)}
          {results.type === 'behavioral_observations' && renderSuggestions(results)}
          {results.type === 'self_reflection_plan' && renderLearningPlan(results)}
          {(results.type === 'time_analysis' || results.type === 'risk_awareness' ||
            results.type === 'performance_review' || results.type === 'search') && renderEnhancedAnalysis(results)}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="loading text-center py-12">
          <div className="inline-flex items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mr-4"></div>
            <span className="text-muted-foreground text-lg">
              🤖 AI is analyzing your trading patterns...
            </span>
          </div>
        </div>
      )}
    </div>
  );

    </div>
 
};

export default AIAnalysis;